/** Spam guard shared by the form UI and the send-form route. Three signals, all
 *  invisible to humans and to assistive tech:
 *
 *  1. honeypot — an off-screen text input; only bots that fill every field touch it
 *  2. token    — a random value written by JS on mount; missing when no JS ran
 *  3. duration — how long the form was open, measured by JS; enforces a minimum
 *
 *  "Honeypot empty AND token present" is much harder to fake than either alone.
 *  All names are plausible-but-unusual on purpose, since bots skip the obvious
 *  `honeypot`/`hp_field`; the honeypot's name additionally rotates per form
 *  instance. Every name lives here only, so all three are free to change. */

/** Candidate names for the off-screen text input; each form instance picks one at
 *  random, so there is no single name to fingerprint. The reader checks every entry,
 *  so the pool can grow or shrink without breaking markup already in the browser.
 *
 *  Two rules when editing: no token a browser's autofill knows (name, company,
 *  phone, fax, email, street, zip, city, country, cc…), otherwise autofill would
 *  fill the trap for a real visitor — and nothing an editor might use as a real
 *  field name in the CMS, since these values never reach the mail. */
const HONEYPOT_FIELDS = [
    'contact_ref_2',
    'subject_alt_2',
    'message_ref_3',
    'inquiry_code_2',
    'request_note_alt',
    'topic_ref_4',
] as const;

/** Random token written by JS on mount. */
export const CLIENT_TOKEN_FIELD = 'session_ref';

/** Milliseconds the form was open, measured by JS between mount and submit. A
 *  duration rather than a load timestamp: it is produced by a single monotonic
 *  clock, so neither clock skew nor a time correction can distort the check. */
export const FILL_DURATION_FIELD = 'session_delta';

/** Anything submitted faster than this is treated as automated — humans need
 *  longer to read the labels and type. */
export const MIN_FILL_DURATION_MS = 1500;

/** The guard's own fields; they carry no content and stay out of the mail. Every
 *  honeypot candidate is listed, not just the one this instance rendered. */
export const SPAM_GUARD_FIELDS = [...HONEYPOT_FIELDS, CLIENT_TOKEN_FIELD, FILL_DURATION_FIELD] as const;

/** Shape of the values createClientToken() produces. */
const TOKEN_PATTERN = /^[a-z0-9-]{16,64}$/i;

export type SpamReason = 'honeypot' | 'missing-token' | 'missing-duration' | 'too-fast';

/** Draws the honeypot name for a single form instance. */
export function pickHoneypotField(): string {
    const index = Math.floor(Math.random() * HONEYPOT_FIELDS.length);
    return HONEYPOT_FIELDS[index] ?? HONEYPOT_FIELDS[0];
}

/** randomUUID needs a secure context; fall back so the token is never empty. */
export function createClientToken(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }

    return `${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
}

/** Returns why a submission looks automated, or null when it looks human. */
export function detectSpam(formData: FormData): SpamReason | null {
    // Which name was rendered is unknown here, so every candidate must be empty.
    const trapped = HONEYPOT_FIELDS.some((name) => {
        const value = formData.get(name);
        return typeof value === 'string' && value.trim() !== '';
    });
    if (trapped) return 'honeypot';

    const token = formData.get(CLIENT_TOKEN_FIELD);
    if (typeof token !== 'string' || !TOKEN_PATTERN.test(token)) return 'missing-token';

    // Missing/empty → Number(null) === 0, a File → NaN; both fail this check.
    const fillDuration = Number(formData.get(FILL_DURATION_FIELD));
    if (!Number.isFinite(fillDuration) || fillDuration <= 0) return 'missing-duration';

    if (fillDuration < MIN_FILL_DURATION_MS) return 'too-fast';

    return null;
}
